import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayIcon from '../../icons/play';
import type { HydratedMediaItem, MediaStatusResult } from './media-type-config';

interface AudioItemContentProps {
  item: HydratedMediaItem;
  statusChecker?: (mediaID: string) => Promise<MediaStatusResult>;
}

const playIconSx = {
  display: 'inline-block',
  maxWidth: 150,
  width: 100,
  cursor: 'pointer',
  pb: '10px',
};

/**
 * Renders a PlayIcon for an audio item and optionally checks upload status
 * on mount via the injected statusChecker. Shows an error indicator if the
 * audio file has not been uploaded. Uses an isMounted ref to prevent state
 * updates after unmount.
 */
const AudioItemContent: React.FC<AudioItemContentProps> = ({ item, statusChecker }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (statusChecker) {
      statusChecker(item.mediaID)
        .then((result) => {
          if (!cancelled && !result.ok) {
            setHasError(true);
          }
        })
        .catch(() => {
          if (!cancelled) setHasError(true);
        });
    }

    return () => { cancelled = true; };
  }, [item.mediaID, statusChecker]);

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Box
        sx={playIconSx}
        onClick={item.onOpenViewer}
        data-testid="audio-play-icon"
      >
        <PlayIcon />
      </Box>
      {hasError && (
        <ErrorOutlineIcon
          data-testid="audio-error-indicator"
          color="error"
          sx={{
            position: 'absolute', top: -4, right: -4, fontSize: 18,
          }}
        />
      )}
    </Box>
  );
};

export default AudioItemContent;
